/* Cliente da API do painel.
 *
 * Um lugar só para três coisas que, espalhadas, viram bug:
 *
 *   1. **`credentials: 'same-origin'`.** O cookie de sessão é HttpOnly; sem
 *      isto o `fetch` não o manda e toda chamada responde 401.
 *   2. **Tradução do erro.** A API responde sempre
 *      `{"erro": {"codigo", "mensagem"}}`. Aqui isso vira uma exceção com o
 *      código legível, para a tela decidir o que mostrar sem repetir `if
 *      (resposta.status === 401)` em cada chamada.
 *   3. **Sessão expirada.** 401 em qualquer chamada é a mesma coisa: a sessão
 *      acabou. Um único assinante leva de volta para a entrada, sem cada tela
 *      precisar tratar.
 */

export class ErroDaApi extends Error {
  constructor(codigo, mensagem, http, detalhes) {
    super(mensagem);
    this.name = 'ErroDaApi';
    // Em português, como o resto da API: é `erro.mensagem` que as telas leem
    // (29 lugares). Só `message` herdado deixava todo aviso sem texto.
    this.mensagem = mensagem;
    this.codigo = codigo;
    this.http = http;
    this.detalhes = detalhes;
  }

  /** O banco não respondeu — dá para tentar de novo. */
  get temporario() {
    return this.codigo === 'banco_indisponivel' || this.http >= 500;
  }
}

let aoExpirar = null;

/** Registra o que fazer quando a sessão cair. Chamado uma vez, na raiz. */
export function quandoExpirar(callback) {
  aoExpirar = callback;
}

async function requisitar(caminho, opcoes = {}) {
  let resposta;

  try {
    resposta = await fetch(caminho, {
      credentials: 'same-origin',
      headers: opcoes.corpo ? { 'Content-Type': 'application/json' } : undefined,
      method: opcoes.metodo || 'GET',
      body: opcoes.corpo ? JSON.stringify(opcoes.corpo) : undefined,
      signal: opcoes.sinal,
    });
  } catch (erro) {
    if (erro.name === 'AbortError') throw erro;

    // Rede fora, servidor caído, DNS. Não é 500 — a requisição nem chegou.
    throw new ErroDaApi(
      'sem_conexao',
      'Não foi possível falar com o servidor. Verifique a conexão.',
      0,
    );
  }

  if (resposta.status === 204) return null;

  let corpo = null;
  try {
    corpo = await resposta.json();
  } catch {
    corpo = null;
  }

  if (!resposta.ok) {
    const erro = corpo?.erro || {};

    // 401 em qualquer rota significa a mesma coisa. Tratar em um lugar evita
    // que uma tela nova esqueça e mostre "erro desconhecido" para uma sessão
    // que só expirou.
    if (resposta.status === 401 && !opcoes.ignorar401 && aoExpirar) {
      aoExpirar();
    }

    throw new ErroDaApi(
      erro.codigo || 'erro_desconhecido',
      erro.mensagem || `A requisição falhou (${resposta.status}).`,
      resposta.status,
      erro.detalhes,
    );
  }

  return corpo;
}

function comParametros(caminho, parametros = {}) {
  const busca = new URLSearchParams();

  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor === undefined || valor === null || valor === '') continue;
    busca.set(chave, valor);
  }

  const consulta = busca.toString();
  return consulta ? `${caminho}?${consulta}` : caminho;
}

export const api = {
  // ── Sessão ──────────────────────────────────────────────────────────────
  entrar: (email, senha) =>
    requisitar('/auth/login', {
      metodo: 'POST',
      corpo: { email, senha },
      ignorar401: true, // credencial errada não é sessão expirada
    }),

  sair: () => requisitar('/auth/logout', { metodo: 'POST' }),

  /** Quem sou eu. É o que a raiz chama para decidir entre entrada e painel. */
  eu: () => requisitar('/auth/me', { ignorar401: true }),

  // ── Conversas ───────────────────────────────────────────────────────────
  tenants: () => requisitar('/api/tenants'),

  conversas: (filtros, sinal) =>
    requisitar(comParametros('/api/conversations', filtros), { sinal }),

  conversa: (id) => requisitar(`/api/conversations/${encodeURIComponent(id)}`),

  mensagens: (id, parametros, sinal) =>
    requisitar(
      comParametros(`/api/conversations/${encodeURIComponent(id)}/messages`, parametros),
      { sinal },
    ),

  responder: (id, texto) =>
    requisitar(`/api/conversations/${encodeURIComponent(id)}/messages`, {
      metodo: 'POST',
      corpo: { texto },
    }),

  assumir: (id, horas) =>
    requisitar(`/api/conversations/${encodeURIComponent(id)}/takeover`, {
      metodo: 'POST',
      corpo: { assumir: true, horas },
    }),

  devolver: (id) =>
    requisitar(`/api/conversations/${encodeURIComponent(id)}/takeover`, {
      metodo: 'POST',
      corpo: { assumir: false },
    }),

  encerrar: (id) =>
    requisitar(`/api/conversations/${encodeURIComponent(id)}/close`, { metodo: 'POST' }),

  /** O que mudou desde o marcador. Devolve `{mudancas, agora}`. */
  atualizacoes: (desde, sinal) =>
    requisitar(comParametros('/api/updates', { desde }), { sinal }),

  // ── Runs ────────────────────────────────────────────────────────────────
  runs: (filtros, sinal) => requisitar(comParametros('/api/runs', filtros), { sinal }),

  run: (id) => requisitar(`/api/runs/${encodeURIComponent(id)}`),

  reprocessar: (id) =>
    requisitar(`/api/runs/${encodeURIComponent(id)}/reprocess`, { metodo: 'POST' }),

  // ── Gestão ──────────────────────────────────────────────────────────────
  metricas: (sinal) => requisitar('/api/metrics/overview', { sinal }),

  auditoria: (limite, sinal) =>
    requisitar(comParametros('/api/audit', { limit: limite }), { sinal }),

  assistentes: (sinal) => requisitar('/api/assistants', { sinal }),

  criarAssistente: (dados) =>
    requisitar('/api/assistants', { metodo: 'POST', corpo: dados }),

  salvarAssistente: (id, mudancas) =>
    requisitar(`/api/assistants/${encodeURIComponent(id)}`, {
      metodo: 'PUT',
      corpo: mudancas,
    }),

  // ── Empresas e servidores (Fase 0) ──────────────────────────────────────
  empresas: (sinal) => requisitar('/api/empresas', { sinal }),

  salvarEmpresa: (id, mudancas) =>
    requisitar(`/api/empresas/${id}`, { metodo: 'PUT', corpo: mudancas }),

  servidores: (sinal) => requisitar('/api/servers', { sinal }),

  criarServidor: (corpo) => requisitar('/api/servers', { metodo: 'POST', corpo }),

  salvarServidor: (id, corpo) =>
    requisitar(`/api/servers/${id}`, { metodo: 'PUT', corpo }),

  removerServidor: (id) => requisitar(`/api/servers/${id}`, { metodo: 'DELETE' }),

  numeros: (sinal) => requisitar('/api/numbers', { sinal }),

  /** Cria o cadastro de um número novo (o nome da instância é gerado). */
  criarNumero: (corpo) => requisitar('/api/numbers', { metodo: 'POST', corpo }),

  salvarNumero: (id, corpo) => requisitar(`/api/numbers/${id}`, { metodo: 'PUT', corpo }),

  /** Apaga a instância na Evolution E o cadastro. As conversas ficam. */
  removerNumero: (id) => requisitar(`/api/numbers/${id}`, { metodo: 'DELETE' }),

  // ── Agentes de IA (do cliente) ──────────────────────────────────────────
  agentes: (sinal) => requisitar('/api/agents', { sinal }),

  criarAgente: (corpo) => requisitar('/api/agents', { metodo: 'POST', corpo }),

  salvarAgente: (id, corpo) => requisitar(`/api/agents/${id}`, { metodo: 'PUT', corpo }),

  // ── Usuários (Fase 0) ───────────────────────────────────────────────────
  usuarios: (sinal) => requisitar('/api/users', { sinal }),

  criarUsuario: (corpo) => requisitar('/api/users', { metodo: 'POST', corpo }),

  salvarUsuario: (id, corpo) => requisitar(`/api/users/${id}`, { metodo: 'PUT', corpo }),

  redefinirSenha: (id) => requisitar(`/api/users/${id}/password`, { metodo: 'POST' }),

  salvarTenantsDoUsuario: (id, senders) =>
    requisitar(`/api/users/${id}/tenants`, { metodo: 'PUT', corpo: { senders } }),

  acoes: (sinal) => requisitar('/api/scheduled-actions', { sinal }),

  salvarAcao: (id, mudancas) =>
    requisitar(`/api/scheduled-actions/${id}`, { metodo: 'PUT', corpo: mudancas }),

  // ── Campanhas de jornada (T11) ──────────────────────────────────────────
  campanhas: (sinal) => requisitar('/api/journeys', { sinal }),

  criarCampanha: (corpo) =>
    requisitar('/api/journeys', { metodo: 'POST', corpo }),

  salvarCampanha: (id, corpo) =>
    requisitar(`/api/journeys/${encodeURIComponent(id)}`, { metodo: 'PUT', corpo }),

  // O endereço próprio do link (`atendimento.<base>`) já funciona? DNS →
  // HTTPS → redirect, com o diagnóstico em português. Não grava nada.
  verificarEnderecoDoLink: (id) =>
    requisitar(`/api/journeys/${encodeURIComponent(id)}/hub/verificar`, { metodo: 'POST' }),

  passosDaCampanha: (id, fase, sinal) =>
    requisitar(comParametros(`/api/journeys/${encodeURIComponent(id)}/steps`, { fase }), { sinal }),

  salvarPassosDaCampanha: (id, passos, fase) =>
    requisitar(`/api/journeys/${encodeURIComponent(id)}/steps`, {
      metodo: 'PUT',
      corpo: { passos, fase },
    }),

  funilDaCampanha: (id, sinal) =>
    requisitar(`/api/journeys/${encodeURIComponent(id)}/funnel`, { sinal }),

  // ── Conexão do WhatsApp ─────────────────────────────────────────────────
  //
  // A conexão é por NÚMERO (`numero_id`), não por assistente: quem conecta o
  // celular é o cliente (Fase D, passo 1). O que continua por assistente é o
  // que é da empresa — a chave da IA (`testarChave`) e as capacidades.
  criarInstancia: (numeroId) =>
    requisitar(`/api/numbers/${numeroId}/instance`, { metodo: 'POST' }),

  /** Pede um QR NOVO. Não use em laço curto: cada chamada reinicia a
   *  negociação com o WhatsApp e troca o código debaixo de quem está lendo. */
  qrcode: (numeroId, sinal) =>
    requisitar(`/api/numbers/${numeroId}/instance/qrcode`, { sinal }),

  /** "Já pareou?" — consulta barata, só de leitura. É esta que vai em laço. */
  estadoDaInstancia: (numeroId, sinal) =>
    requisitar(`/api/numbers/${numeroId}/instance/estado`, { sinal }),

  verificarInstancia: (numeroId) =>
    requisitar(`/api/numbers/${numeroId}/instance/check`, { metodo: 'POST' }),

  testarWebhook: (numeroId) =>
    requisitar(`/api/numbers/${numeroId}/instance/webhook-test`, { metodo: 'POST' }),

  reapontarWebhook: (numeroId) =>
    requisitar(`/api/numbers/${numeroId}/instance/webhook`, { metodo: 'POST' }),

  desconectarInstancia: (numeroId) =>
    requisitar(`/api/numbers/${numeroId}/instance`, { metodo: 'DELETE' }),

  testarChave: (id) =>
    requisitar(`/api/assistants/${encodeURIComponent(id)}/test`, { metodo: 'POST' }),

  /** A senha nunca volta — só `senha_preenchida`. */
  proxyDaInstancia: (numeroId, sinal) =>
    requisitar(`/api/numbers/${numeroId}/instance/proxy`, { sinal }),

  salvarProxy: (numeroId, proxy) =>
    requisitar(`/api/numbers/${numeroId}/instance/proxy`, {
      metodo: 'PUT',
      corpo: proxy,
    }),

  // ── Saúde e capacidades ─────────────────────────────────────────────────
  saude: (sinal) => requisitar('/api/health', { sinal }),

  catalogoDeTools: (sinal) => requisitar('/api/tools', { sinal }),

  salvarTools: (id, tools, config) =>
    requisitar(`/api/assistants/${encodeURIComponent(id)}/tools`, {
      metodo: 'PUT',
      corpo: { tools, config },
    }),
};
